// libraries/components/src/components/upcoming-trips/features/use-itinerary-store.ts
"use client";

import { useContext } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSearchParams } from "next/navigation";

import { useManageTripsConfig } from "@jb-manage-trips/runtime-config";

import {
  itineraryStateTransform,
  type ItineraryImageOptions,
} from "../services/itinerary-state-transform";

import type {
  ItineraryServiceResponse,
  ItineraryState,
  PnrState,
  UpcomingTripsApiResponse,
} from "@jb-manage-trips/types/itinerary";

import { AuthContext } from "../../../../../../apps/managetrips/src/components/auth-wrapper/auth-context";

import {
  UPCOMING_TRIPS_QUERY_KEY,
  fetchUpcomingTrips,
  USE_UPCOMING_TRIPS_MOCKS,
} from "../../shared/upcoming-trips";

import {
  HEALTH_KEYS,
  callHealth,
  type HealthResponse,
} from "@jb-manage-trips/api/mutations/health";

// Image options mirroring what Angular used via AppConfigService + CMS
const IMAGE_OPTIONS: ItineraryImageOptions = {
  cmsHostname: process.env.NEXT_PUBLIC_CMS_HOSTNAME ?? "cmsnprd.jetblue.com",
  flightBlockImagePrefix:
    process.env.NEXT_PUBLIC_ITINERARY_IMAGE_PREFIX ??
    "public/dotcom/flight-block-images",
};

// IMPORTANT: NEXT_PUBLIC_* env vars are inlined at build time.
const USE_DEV_MOCKS =
  // eslint-disable-next-line no-implicit-coercion -- preferred this pattern
  !!process.env.NEXT_PUBLIC_USE_DEV_MOCKS &&
  process.env.NEXT_PUBLIC_USE_DEV_MOCKS.toLowerCase() === "true";
// eslint-disable-next-line no-implicit-coercion -- preferred this pattern
const BYPASS_AUTH = !!process.env.NEXT_PUBLIC_LOCAL_PNR_HEALTH_URL;

/**
 * useItineraryStore
 *
 * - Authenticated users:
 *   - Use UPCOMING_TRIPS_QUERY_KEY backed by ReservationsByTB (Sabre DC).
 * - Anonymous users:
 *   - If lastName + pnr are in the URL, call /health-check again (or reuse its cache)
 *     and map that HealthResponse into an UpcomingTrips-like shape.
 *   - This allows anon itineraries to survive hard refreshes.
 * - In dev-mock mode (NEXT_PUBLIC_USE_DEV_MOCKS=true):
 *   - TB/SSG fetchUpcomingTrips is disabled; only health or seeded cache is used.
 */
export const useItineraryStore = (): UseQueryResult<ItineraryState[]> => {
  const auth = useContext(AuthContext);
  const config = useManageTripsConfig() as {
    MANAGE_TRIPS?: {
      API?: {
        RESERVATIONS_BY_TB_URL?: string;
        HEALTH_CHECK_URL?: string;
      };
    };
  };
  const searchParameters = useSearchParams();

  const accessToken = auth.ACCESS_TOKEN ?? "";
  const reservationsByTbUrl =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typescript complains
    config?.MANAGE_TRIPS?.API?.RESERVATIONS_BY_TB_URL ?? "";
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typescript complains
  const healthCheckUrl = config?.MANAGE_TRIPS?.API?.HEALTH_CHECK_URL ?? "";
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typescript complains
  const lastNameFromQuery = searchParameters?.get("lastName") ?? "";
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typescript complains
  const pnrFromQuery = searchParameters?.get("pnr") ?? "";

  const isAuth = Boolean(accessToken);
  const localHealthUrl = process.env.NEXT_PUBLIC_LOCAL_PNR_HEALTH_URL ?? "";
  const effectiveHealthUrl = localHealthUrl || healthCheckUrl;

  const hasLocatorParameters =
    Boolean(lastNameFromQuery) &&
    Boolean(pnrFromQuery) &&
    Boolean(effectiveHealthUrl);

  // If USE_UPCOMING_TRIPS_MOCKS is true, hits CB mock JSON API irrespective of auth status for authenticated flow
  const useNetworkMockedCrystalBlue = USE_DEV_MOCKS && USE_UPCOMING_TRIPS_MOCKS;
  const useAuthCrystalBlue =
    !USE_UPCOMING_TRIPS_MOCKS && accessToken && reservationsByTbUrl;

  // 1) Upcoming-trips query auth flow
  const authEnabled = Boolean(
    !BYPASS_AUTH && (useNetworkMockedCrystalBlue || useAuthCrystalBlue),
  );

  const tbQuery = useQuery({
    queryKey: UPCOMING_TRIPS_QUERY_KEY,
    enabled: authEnabled,
    queryFn: async (): Promise<UpcomingTripsApiResponse> =>
      fetchUpcomingTrips({
        url: reservationsByTbUrl,
        accessToken,
      }),
    staleTime: 60_000, // 1 minute
    refetchOnWindowFocus: false,
  });

  // 2) Health-check anon flow with locator params
  const healthEnabled = !isAuth && hasLocatorParameters;

  const healthQuery = useQuery({
    queryKey: HEALTH_KEYS.byReq({
      lastName: lastNameFromQuery,
      b6PNRRecordLocator: pnrFromQuery,
    }),
    enabled: healthEnabled,
    queryFn: async () =>
      callHealth({
        url: effectiveHealthUrl,
        request: {
          lastName: lastNameFromQuery,
          b6PNRRecordLocator: pnrFromQuery,
        },
      }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  }) as UseQueryResult<HealthResponse>;

  // Prefer TB data when available; fall back to health-check itinerary
  let sourceTrips: ItineraryServiceResponse[] =
    [] as ItineraryServiceResponse[];

  if (tbQuery.data && tbQuery.data.trips.length > 0) {
    sourceTrips = tbQuery.data.trips;
  } else if (healthQuery.data?.pnrItinAirSeg?.length) {
    const h = healthQuery.data;
    const healthTrip: ItineraryServiceResponse = {
      b6PNRRecordLocator: h.b6PNRRecordLocator ?? pnrFromQuery,
      pnrState: h.pnrState as PnrState,
      pnrItinAirSeg: h.pnrItinAirSeg ?? [],
      emailExist: h.emailExist,
      phoneNumberExist: h.phoneNumberExist,
      lastName: lastNameFromQuery,
    };

    sourceTrips = [healthTrip];
  }
  const trips = itineraryStateTransform(sourceTrips, IMAGE_OPTIONS);

  const activeQuery = authEnabled ? tbQuery : healthQuery;

  return {
    ...activeQuery,
    data: trips,
  } as unknown as UseQueryResult<ItineraryState[]>;
};
